import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, finalize, Subject, takeUntil } from 'rxjs';
import { Menu, NavService } from 'src/app/shared/services/nav.service';
import { SwitcherService } from 'src/app/shared/services/switcher.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/shared/services/auth.service';
import { FirstAccessService } from 'src/app/shared/services/first-access.service';

@Component({
  selector: 'app-content-layout',
  templateUrl: './content-layout.component.html',
  styleUrls: ['./content-layout.component.scss'],
})
export class ContentLayoutComponent implements OnInit, OnDestroy {
  @ViewChild('changePasswordModal') changePasswordModal!: TemplateRef<any>;
  @ViewChild('modalFirstAccess') modalFirstAccess!: TemplateRef<any>;

  public menuItems!: Menu[];
  public mode!: string;

  public novaSenha = '';
  public confirmarNovaSenha = '';
  public errorMessage = '';
  public isChangingPassword = false;

  private destroy$ = new Subject<void>();
  private changePasswordModalRef?: NgbModalRef;
  private successModalRef?: NgbModalRef;
  private firstAccessAlreadyOpened = false;

  constructor(
    public SwitcherService: SwitcherService,
    public navServices: NavService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private authService: AuthService,
    private firstAccessService: FirstAccessService
  ) {
    this.navServices.items.subscribe((menuItems: any) => {
      this.menuItems = menuItems;
    });
  }

  ngOnInit(): void {
    document.querySelector('.slide-leftRTL')?.classList.add('d-none');
    document.querySelector('.slide-rightRTL')?.classList.add('d-none');

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const childRoute = this.getDeepestChild(this.route);
        this.mode = childRoute.snapshot.data['mode'] ?? '';
      });

    this.firstAccessService.mustChangePassword$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mustOpen) => {
        if (!mustOpen || this.firstAccessAlreadyOpened) return;

        this.firstAccessAlreadyOpened = true;

        setTimeout(() => {
          this.openChangePasswordModal();
        });
      });

    if (
      this.authService.userIsAuth() &&
      this.authService.needChangePassword() &&
      !this.firstAccessAlreadyOpened
    ) {
      this.firstAccessAlreadyOpened = true;

      setTimeout(() => {
        this.openChangePasswordModal();
      });
    }
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    let current = route;
    while (current.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private openChangePasswordModal(): void {
    if (this.changePasswordModalRef) return;

    this.errorMessage = '';
    this.novaSenha = '';
    this.confirmarNovaSenha = '';

    this.changePasswordModalRef = this.modalService.open(
      this.changePasswordModal,
      {
        backdrop: 'static',
        keyboard: false,
        centered: true,
        size: 'md',
      }
    );

    this.changePasswordModalRef.result.finally(() => {
      this.changePasswordModalRef = undefined;
    });
  }

  changePassword(): void {
    if (this.isChangingPassword) return;

    const novaSenha = (this.novaSenha || '').trim();
    const confirmarNovaSenha = (this.confirmarNovaSenha || '').trim();

    this.errorMessage = '';

    if (!novaSenha || !confirmarNovaSenha) {
      this.errorMessage = 'Preencha os dois campos.';
      return;
    }

    if (novaSenha !== confirmarNovaSenha) {
      this.errorMessage = 'As senhas não coincidem.';
      return;
    }

    if (novaSenha.length < 8) {
      this.errorMessage = 'A senha precisa ter no mínimo 8 caracteres.';
      return;
    }

    this.isChangingPassword = true;

    this.authService
      .updatePassword(novaSenha)
      .pipe(
        finalize(() => {
          this.isChangingPassword = false;
        })
      )
      .subscribe({
        next: () => {
          this.authService.changeSecurityStep('SET_PASSWORD', true);

          this.changePasswordModalRef?.close();

          this.successModalRef = this.modalService.open(this.modalFirstAccess, {
            backdrop: 'static',
            keyboard: false,
            centered: true,
            size: 'sm',
          });
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.mensagem || 'Erro ao alterar a senha.';
        },
      });
  }

  resetLogin(): void {
    this.successModalRef?.close();
    this.firstAccessService.clear();

    this.authService.revokeToken();
    this.authService.clearStorage();

    this.router.navigate(['/']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  scrolled: boolean = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.scrolled = window.scrollY > 74;
  }
}